import React, { useState, useEffect, useRef } from 'react';
import ProgressMonitor from '../utils/ProgressMonitor';
import './FaceRecognitionProgress.css';

/**
 * Face Recognition Progress Component
 * Displays real-time progress for face recognition analysis
 */
const FaceRecognitionProgress = ({ 
  sessionId, 
  isRunning, 
  onComplete, 
  onError,
  baseUrl = '/api'
}) => {
  const [progress, setProgress] = useState({
    currentIndex: 0,
    totalCount: 0,
    currentPhotoName: null,
    statusMessage: 'Initializing...',
    percentage: 0
  });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const progressMonitorRef = useRef(null);

  useEffect(() => {
    if (isRunning && sessionId) {
      startProgressMonitoring();
    } else {
      stopProgressMonitoring();
    }

    // Cleanup on unmount
    return () => {
      stopProgressMonitoring();
    };
  }, [isRunning, sessionId]);

  const startProgressMonitoring = () => {
    if (progressMonitorRef.current) {
      stopProgressMonitoring();
    }

    console.log('🚀 Starting progress monitoring for session:', sessionId);
    
    const monitor = new ProgressMonitor(sessionId, baseUrl);
    progressMonitorRef.current = monitor;

    monitor
      .onConnected((data) => {
        console.log('✅ Connected to progress stream:', data);
        setIsConnected(true);
        setError(null);
      })
      .onProgress((data) => {
        console.log('📊 Progress update:', data);
        setProgress({
          currentIndex: data.currentIndex || 0,
          totalCount: data.totalCount || 0,
          currentPhotoName: data.currentPhotoName,
          statusMessage: data.statusMessage || 'Processing...',
          percentage: data.percentage || 0
        });
      })
      .onComplete((data) => {
        console.log('✅ Process completed:', data);
        setProgress(prev => ({
          ...prev,
          statusMessage: 'Analysis complete!',
          percentage: 100
        }));
        setResult(data.result);
        
        if (onComplete) {
          onComplete(data.result);
        }
        
        stopProgressMonitoring();
      })
      .onError((data) => {
        console.error('❌ Progress error:', data);
        setError(data.message || 'An error occurred during processing');
        
        if (onError) {
          onError(data);
        }
        
        stopProgressMonitoring();
      });

    monitor.start();
  };

  const stopProgressMonitoring = () => {
    if (progressMonitorRef.current) {
      console.log('🛑 Stopping progress monitoring');
      progressMonitorRef.current.stop();
      progressMonitorRef.current = null;
      setIsConnected(false);
    }
  };

  const formatPhotoName = (photoName) => {
    if (!photoName) return '';
    // Truncate long filenames
    if (photoName.length > 30) {
      return photoName.substring(0, 27) + '...';
    }
    return photoName;
  };

  if (!isRunning) {
    return null;
  }

  return (
    <div className="face-recognition-progress">
      <div className="progress-header">
        <h3>Face Recognition Analysis</h3>
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
        </div>
      </div>

      {error && (
        <div className="progress-error">
          <span className="error-icon">❌</span>
          <span className="error-message">{error}</span>
        </div>
      )}

      <div className="progress-content">
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="progress-percentage">
            {progress.percentage.toFixed(0)}%
          </div>
        </div>

        <div className="progress-details">
          <div className="status-message">
            {progress.statusMessage}
          </div>
          
          {progress.totalCount > 0 && (
            <div className="photo-progress">
              Processing photo {progress.currentIndex + 1} of {progress.totalCount}
            </div>
          )}
          
          {progress.currentPhotoName && (
            <div className="current-photo">
              Current: {formatPhotoName(progress.currentPhotoName)}
            </div>
          )}
        </div>

        {result && (
          <div className="progress-result">
            <h4>Analysis Complete!</h4>
            <div className="result-summary">
              <div className="result-stat">
                <span className="stat-label">Matches Found:</span>
                <span className="stat-value">{result.matches?.length || 0}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Reference Persons:</span>
                <span className="stat-value">{result.referencePersons?.length || 0}</span>
              </div>
              {result.savedToDatabase && (
                <div className="result-stat">
                  <span className="stat-label">Saved to Database:</span>
                  <span className="stat-value">{result.savedToDatabase}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceRecognitionProgress;
